import React, { useEffect, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ManualLoader, Wrapper } from "../../components";
import { ContentContainer, PageHeader } from "src/components/Layouts/AppShell";
import { useNavigate } from "react-router-dom";
import {
  useGetDocsQuery,
  useGetMoveInConditionStatusQuery,
  useLazyGetDocQuery,
  useGetUserQuery,
} from "../../store/services/privateApi";
import { isRenewalOfferDoc, getCurrentLease } from "../../helpers/helperLease";
import { Box, ListItemButton, ListItemIcon } from "@mui/material";
import { MoveInConditionStatusResponse } from "src/shared/models/containers/moveInCondition.model";

const MOVE_IN_CONDITION_DOCS_PATH = "/Move-In Condition Forms";
const RESMAN_VISIBLE_DOC_PATHS = [
  "/Signed Lease Documents",
  "/Lease Documents",
  MOVE_IN_CONDITION_DOCS_PATH,
];

type DocsListItem = {
  dateAttached?: string;
  documentId?: string;
  name: string;
  path: string;
  uri?: string;
};

const appendDoc = (
  map: Map<string, DocsListItem[]>,
  path: string,
  doc: DocsListItem
) => {
  const items = map.get(path) ?? [];
  items.push(doc);
  map.set(path, items);
};

const appendMoveInConditionDocs = (
  map: Map<string, DocsListItem[]>,
  moveInConditionStatus?: MoveInConditionStatusResponse
) => {
  if (!moveInConditionStatus?.submitted) return;

  const {
    countersignedAt,
    countersignedPdfUrl,
    createdAt,
    id,
    pdfGeneratedAt,
    pdfUrl,
  } = moveInConditionStatus;

  if (countersignedPdfUrl) {
    appendDoc(map, MOVE_IN_CONDITION_DOCS_PATH, {
      dateAttached: countersignedAt ?? createdAt,
      documentId: `move-in-condition-${id ?? "latest"}-executed`,
      name: "Move-In Condition Form - Executed Copy.pdf",
      path: MOVE_IN_CONDITION_DOCS_PATH,
      uri: countersignedPdfUrl,
    });
  }

  if (pdfUrl) {
    appendDoc(map, MOVE_IN_CONDITION_DOCS_PATH, {
      dateAttached: pdfGeneratedAt ?? createdAt,
      documentId: `move-in-condition-${id ?? "latest"}-resident`,
      name: countersignedPdfUrl
        ? "Move-In Condition Form - Resident Copy.pdf"
        : "Move-In Condition Form.pdf",
      path: MOVE_IN_CONDITION_DOCS_PATH,
      uri: pdfUrl,
    });
  }
};

const Docs = () => {
  const { data: docs, isLoading } = useGetDocsQuery(null);
  const [getLazyDocQuery] = useLazyGetDocQuery();
  const { data: userData }: any = useGetUserQuery(null);

  const currentLease = getCurrentLease(userData);
  const { data: moveInConditionStatus } = useGetMoveInConditionStatusQuery(
    currentLease?.id ?? "",
    { skip: !currentLease?.id }
  );

  const [docsData, setDocsData] = useState<Map<string, DocsListItem[]>>(
    new Map()
  );
  useEffect(() => {
    const map = new Map<string, DocsListItem[]>();
    docs?.forEach((doc: any) => {
      const { path } = doc;
      if (RESMAN_VISIBLE_DOC_PATHS.indexOf(path) > -1) {
        appendDoc(map, path, doc);
      } else if (
        currentLease &&
        isRenewalOfferDoc(doc, currentLease.LeaseEndDate)
      ) {
        appendDoc(map, "Renewal Offer", doc);
      }
    });
    appendMoveInConditionDocs(map, moveInConditionStatus);
    setDocsData(map);
  }, [docs, currentLease, moveInConditionStatus]);
  const navigate = useNavigate();
  return (
    <>
      {isLoading && <ManualLoader />}
      <Wrapper isAuthorizedWrapper idSelector={"wrapperContainer"}>
        <ContentContainer size="narrow">
          <PageHeader
            title={"Documents"}
            onBack={() => navigate("/settings")}
          />
          {!isLoading && docsData.size === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <Typography color="text.secondary">
                No documents available
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {Array.from(docsData).map(([subCategory, items]) => (
                <Accordion
                  key={subCategory}
                  defaultExpanded
                  disableGutters
                  sx={{
                    borderRadius: "12px !important",
                    border: "1px solid #e0e0e0",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "#E0F0F2",
                      minHeight: 48,
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        gap: 1,
                      },
                    }}
                  >
                    <FolderOutlinedIcon
                      sx={{ color: "text.secondary", fontSize: 20 }}
                    />
                    <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                      {subCategory.replace(/^\//, "")}
                    </Typography>
                    <Typography
                      sx={{ ml: 1, color: "text.secondary", fontSize: "13px" }}
                    >
                      ({items.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0 }}>
                    {items.map((item, index) => (
                      <ListItemButton
                        key={item.documentId || item.uri || index}
                        onClick={async () => {
                          if (item.uri) {
                            window.open(item.uri, "_blank");
                            return;
                          }
                          if (!item.documentId) return;

                          const res: any = await getLazyDocQuery(
                            item.documentId
                          );
                          if (!res.isError) {
                            window.open(res.data.uri, "_blank");
                          }
                        }}
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderTop: index > 0 ? "1px solid #f0f0f0" : "none",
                          "&:hover": { backgroundColor: "#fafafa" },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <DescriptionOutlinedIcon
                            sx={{ color: "primary.main", fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.name}
                          </Typography>
                          {item.dateAttached && (
                            <Typography
                              sx={{ fontSize: "12px", color: "text.secondary" }}
                            >
                              {new Date(item.dateAttached).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                        <OpenInNewIcon
                          sx={{ color: "text.secondary", fontSize: 16, ml: 1 }}
                        />
                      </ListItemButton>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </ContentContainer>
      </Wrapper>
    </>
  );
};

export default Docs;
